import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

// Where a role lands after authenticating.
export const homeForRole = (role) => (role === "driver" ? "/portal" : "/board");

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, role: data.role };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const driverLogin = async (phone, password) => {
    try {
      const { data } = await api.post("/auth/driver/login", { phone, password });
      setUser(data);
      return { ok: true, role: data.role };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const driverClaim = async (phone, password, name) => {
    try {
      const { data } = await api.post("/auth/driver/claim", { phone, password, name });
      setUser(data);
      return { ok: true, role: data.role };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed (clearing session anyway):", e?.message || e);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, driverLogin, driverClaim, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
